<script setup>
import { defineProps, defineEmits, computed, ref, watch } from 'vue';
import { getUndefinedVariablesInMessage } from '@chatwoot/utils';

import { CHAR_LENGTH_WARNING } from 'dashboard/components/widgets/WootWriter/constants.js';
import Modal from 'dashboard/components/Modal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  maxLength: {
    type: Number,
    default: 5000,
  },
  template: {
    type: String,
    default: '',
  },
  variables: {
    type: Object,
    default: () => {},
  },
  missingVariables: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['onClose', 'onSubmit', 'update:show']);

const show = ref(props.show);
const submittedVariables = ref({});

const closeModal = () => {
  show.value = false;
  emit('update:show', false);
  emit('onClose');
};

const allVariables = () => {
  return { ...props.variables, ...submittedVariables.value };
};

const submitVariables = () => {
  let updatedMessage = props.template;
  const variableValues = allVariables();
  const variableNames = updatedMessage.match(/{{(.*?)}}/g) || [];
  variableNames.forEach(varWithBraces => {
    const varName = varWithBraces.replace('{{', '').replace('}}', '').trim();
    const varValue = variableValues[varName] || '';
    updatedMessage = updatedMessage.replace(
      new RegExp(`{{\\s*${varName}\\s*}}`, 'g'),
      varValue
    );
  });

  emit('onSubmit', updatedMessage);
  closeModal();
};

const isFormValid = () => {
  return props.missingVariables.value.every(variable => {
    return submittedVariables.value[variable].toString().trim().length > 0;
  });
};

const trySubmitOnEnter = e => {
  if (e.key === 'Enter' && isFormValid()) {
    submitVariables();
  }
  if (e.key === 'Escape') {
    closeModal();
  }
};

const livePreviewMessage = computed(() => {
  const templateVariables = getUndefinedVariablesInMessage({
    message: props.template,
    variables: {},
  });
  const variableValues = allVariables();

  let previewMessage = props.template;
  templateVariables.forEach(varName => {
    const varValue = variableValues[varName];
    const varDisplay = varValue
      ? `<span class="px-1 rounded bg-woot-100 dark:bg-woot-700">${varValue}</span>`
      : `<span class="px-1 rounded bg-red-100 dark:bg-red-700 text-red-700 dark:text-white">[${varName}]</span>`;
    previewMessage = previewMessage.replace(`{{${varName}}}`, varDisplay);
  });
  return previewMessage;
});

const livePreviewMessageLength = () => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(livePreviewMessage.value, 'text/html');
  return doc.body.textContent.length;
};

const isMessageLengthReachingThreshold = () => {
  return livePreviewMessageLength() > props.maxLength - 50;
};

const charactersRemaining = () => {
  return props.maxLength - livePreviewMessageLength();
};

const disableSubmitButton = () => {
  return charactersRemaining() < 0;
};

const characterLengthWarning = computed(() => {
  return charactersRemaining() < 0
    ? `${-charactersRemaining()} ${CHAR_LENGTH_WARNING.NEGATIVE}`
    : `${charactersRemaining()} ${CHAR_LENGTH_WARNING.UNDER_50}`;
});

const charLengthClass = computed(() => {
  return charactersRemaining() < 0 ? 'text-red-600' : 'text-slate-600';
});

const titleizeVariable = varName => {
  return varName
    .replace(/[_.-]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/\b\w/g, char => char.toUpperCase());
};

watch(
  () => props.show,
  newVal => {
    show.value = newVal;
  }
);
</script>

<template>
  <Modal v-model:show="show" :on-close="closeModal" size="!w-[70rem]">
    <form
      class="flex flex-col space-y-4 w-full p-6 max-h-[90vh] overflow-y-auto"
      @submit.prevent="submitVariables"
      @keydown.enter.exact="trySubmitOnEnter($event)"
      @keydown.esc="closeModal"
    >
      <h2 class="text-xl font-semibold text-slate-900 dark:text-white">
        {{ $t('ONBOARDING.CANNED_RESPONSES.ENTER_VARIABLE_VALUES') }}
      </h2>
      <div class="flex flex-row gap-2">
        <div
          class="flex flex-1 flex-col overflow-y-auto max-h-[calc(100vh-15rem)] px-2"
        >
          <div v-for="key in missingVariables" :key="key" class="flex flex-col">
            <label
              :for="key"
              class="mb-1 text-sm font-medium text-slate-700 dark:text-slate-300 capitalize"
            >
              {{ titleizeVariable(key) }}
            </label>
            <textarea
              :key="key"
              v-model="submittedVariables[key]"
              class="input-variable resize-y"
              :placeholder="
                $t('ONBOARDING.CANNED_RESPONSES.VARIABLE_PLACEHOLDER', {
                  variable: key,
                })
              "
              @keydown.stop
            />
          </div>
        </div>
        <div class="flex flex-1 flex-col">
          <h3
            class="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-2 flex justify-between items-center"
          >
            <span>
              {{ $t('ONBOARDING.CANNED_RESPONSES.PREVIEW_MESSAGE') }}
            </span>
            <span
              v-if="isMessageLengthReachingThreshold"
              class="text-xs text-end font-normal"
              :class="charLengthClass"
            >
              {{ characterLengthWarning }}
            </span>
          </h3>
          <div
            class="rounded-md bg-slate-50 dark:bg-slate-800 dark:border-slate-600"
          >
            <div
              class="p-4 text-xs text-slate-800 dark:text-slate-100 whitespace-pre-wrap overflow-y-auto max-h-[calc(100vh-20rem)]"
              v-html="livePreviewMessage"
            />
          </div>
        </div>
      </div>

      <div class="flex justify-end space-x-2">
        <button type="button" class="button clear" @click.prevent="closeModal">
          {{ $t('ONBOARDING.CANNED_RESPONSES.CANCEL') }}
        </button>
        <NextButton
          :label="$t('ONBOARDING.CANNED_RESPONSES.CONFIRM')"
          :disabled="!isFormValid || disableSubmitButton()"
          :is-loading="false"
          @click.prevent="submitVariables"
        />
      </div>
    </form>
  </Modal>
</template>

<style lang="scss">
.input-variable {
  @apply border rounded-md p-2 text-sm h-[3rem] max-h-[10rem] min-h-[2rem];

  &:focus {
    @apply border-woot-500 ring-1 ring-woot-500 dark:border-woot-400 dark:ring-woot-400 outline-none;
  }
}
</style>
